import React from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutGrid, FileText, Shield, Activity, ClipboardList, /* RefreshCw, */ Lock, CalendarClock, FilePen, BrainCircuit, MessageSquare, Home, Upload, CalendarDays, BookOpen, Sparkles, Wrench, Layers, Send, Mail, MapPin, Terminal, Users } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { PERMISSIONS } from '../types/permissions';

function linkClass({ isActive }: { isActive: boolean }) {
  return isActive ? 'nav-link active' : 'nav-link';
}

export default function Sidebar() {
  const { ready } = useAuth();
  const perms = usePermissions();

  // Return an empty aside so the grid column is always occupied — prevents
  // .main from collapsing into column 1 while auth/perms are loading.
  if (!ready || !perms.ready) return <aside className="sidebar" />;

  const { hasPermission } = perms;

  return (
    <aside className="sidebar">
      <div className="logo">
        <img src={`${process.env.PUBLIC_URL}/KeithBotfieldWebFavicon.svg`} alt="Botfield" />
        <div className="title">Admin HQ</div>
      </div>

      <NavLink to="/" end className={linkClass}>
        <Home size={18} /> Home
      </NavLink>

      {hasPermission(PERMISSIONS.NAV_PORTAL) && (
        <div className="nav-group">
          <div className="group-title">Admin HQ</div>
          <NavLink to="/portal/jobs" className={linkClass}>
            <LayoutGrid size={18} /> Return Jobs
          </NavLink>
          <NavLink to="/portal/extensions" className={linkClass}>
            <CalendarClock size={18} /> Returns to Extend
          </NavLink>
          {hasPermission(PERMISSIONS.NAV_FOLLOWUP) && (
            <NavLink to="/portal/followup" className={linkClass}>
              <ClipboardList size={18} /> Follow-Up Hub
            </NavLink>
          )}
          {/* Knuula Client Push — disabled
          {hasPermission(PERMISSIONS.NAV_FOLLOWUP) && (
            <NavLink to="/portal/clientpush" className={linkClass}>
              <RefreshCw size={18} /> Knuula Client Push
            </NavLink>
          )}
          */}
          {hasPermission(PERMISSIONS.NAV_PTO) && (
            <NavLink to="/pto" className={linkClass}>
              <CalendarDays size={18} /> PTO Dashboard
            </NavLink>
          )}
        </div>
      )}

      {hasPermission(PERMISSIONS.NAV_POA) && (
        <div className="nav-group">
          <div className="group-title">POA</div>
          <NavLink to="/poa" end className={linkClass}>
            <ClipboardList size={18} /> All POAs
          </NavLink>
          <NavLink to="/poa/new" className={linkClass}>
            <FilePen size={18} /> New Form 2848
          </NavLink>
          <NavLink to="/poa/contractors" className={linkClass}>
            <Users size={18} /> Contractors
          </NavLink>
        </div>
      )}

      {(hasPermission(PERMISSIONS.NAV_AI_REVIEW) || hasPermission(PERMISSIONS.NAV_AI_CHAT) || hasPermission(PERMISSIONS.NAV_ASK_KEITH)) && (
        <div className="nav-group">
          <div className="group-title">AI Tools</div>
          {hasPermission(PERMISSIONS.NAV_AI_REVIEW) && (
            <NavLink to="/ai-review" end className={linkClass}>
              <BrainCircuit size={18} /> Return Review
            </NavLink>
          )}
          {hasPermission(PERMISSIONS.NAV_AI_CHAT) && (
            <NavLink to="/ai-review/chat" className={linkClass}>
              <MessageSquare size={18} /> Return Chat
            </NavLink>
          )}
          {hasPermission(PERMISSIONS.NAV_ASK_KEITH) && (
            <NavLink to="/ai-review/askkeith" className={linkClass}>
              <Sparkles size={18} /> AskKeith
            </NavLink>
          )}
        </div>
      )}

      {(hasPermission(PERMISSIONS.NAV_IMPORT) || hasPermission(PERMISSIONS.NAV_MEMBER_STANDARDS)) && (
        <div className="nav-group">
          <div className="group-title">Data Tools</div>
          {hasPermission(PERMISSIONS.NAV_IMPORT) && (
            /* `end` so this doesn't stay highlighted on the nested /import/addresses route */
            <NavLink to="/import" end className={linkClass}>
              <Upload size={18} /> Import Data
            </NavLink>
          )}
          {hasPermission(PERMISSIONS.NAV_IMPORT) && (
            <NavLink to="/import/addresses" className={linkClass}>
              <MapPin size={18} /> Address Validator
            </NavLink>
          )}
          {hasPermission(PERMISSIONS.NAV_IMPORT) && (
            <NavLink to="/utility/ms990ext" className={linkClass}>
              <Wrench size={18} /> MS 990 Batch Extension
            </NavLink>
          )}
          {hasPermission(PERMISSIONS.NAV_MEMBER_STANDARDS) && (
            <NavLink to="/utility/member-standards" className={linkClass}>
              <Mail size={18} /> Member Standards Emails
            </NavLink>
          )}
        </div>
      )}

      {hasPermission(PERMISSIONS.NAV_REPORTS) && (
        <div className="nav-group">
          <div className="group-title">Reports</div>
          <NavLink to="/reports" className={linkClass}>
            <FileText size={18} /> All Existing Reports
          </NavLink>
          {hasPermission(PERMISSIONS.NAV_OUTSOURCED) && (
            <NavLink to="/outsourced" className={linkClass}>
              <FileText size={18} /> Returns Outsourced
            </NavLink>
          )}
        </div>
      )}

      {perms.isAdmin && (
        <div className="nav-group">
          <div className="group-title">Dev Team</div>
          <NavLink to="/admin/dev-team" className={linkClass}>
            <Terminal size={18} /> Dev Team
          </NavLink>
        </div>
      )}

      {(perms.isAdminLeadership || perms.isAdminTeam || perms.isAdmin) && (
        <div className="nav-group">
          <div className="group-title">Admin</div>
          {perms.isAdminLeadership && (
            <NavLink to="/admin/desks" className={linkClass}>
              <LayoutGrid size={18} /> Desks &amp; Assignments
            </NavLink>
          )}
          {(perms.isAdminLeadership || perms.isAdminTeam) && (
            <NavLink to="/admin/team" className={linkClass}>
              <LayoutGrid size={18} /> Admin Team Dashboard
            </NavLink>
          )}
          {perms.isAdmin && (
            <>
              <NavLink to="/admin/token-management" className={linkClass}>
                <Shield size={18} /> Token Management
              </NavLink>
              <NavLink to="/admin/data-integrity" className={linkClass}>
                <Shield size={18} /> Data Integrity
              </NavLink>
              <NavLink to="/admin/health-check" className={linkClass}>
                <Activity size={18} /> Automation Health
              </NavLink>
              <NavLink to="/admin/security" className={linkClass}>
                <Lock size={18} /> Security
              </NavLink>
              <NavLink to="/admin/network-policies" className={linkClass}>
                <Shield size={18} /> Network Policies
              </NavLink>
              <NavLink to="/admin/tax-research-rag" className={linkClass}>
                <BookOpen size={18} /> Tax Research RAG
              </NavLink>
              <NavLink to="/admin/zoom-broadcast" className={linkClass}>
                <Send size={18} /> Zoom Broadcast
              </NavLink>
              <NavLink to="/admin/sandbox" className={linkClass}>
                <Layers size={18} /> Sandbox
              </NavLink>
            </>
          )}
        </div>
      )}

      <div className="footer">© 2026 BMSS, LLC</div>
    </aside>
  );
}
